import type {
  ContextWindowUsage,
  ConversationCompactionKind,
  ConversationDebugDTO,
  ConversationDebugMessageDTO,
  ConversationDebugToolCallDTO,
  LLMModels,
} from "@shaarp/common";
import type { SegmentKey } from "./context-segments";
import type { ContextGap, SegmentRow } from "./context-segments";
import {
  LINE_ENDING,
  escapeCell,
  formatBounds,
  formatBytes,
  formatDate,
  formatDelta,
  formatPercent,
  fullNumber,
} from "./format";
import type { Incident } from "./incidents";
import { isSearchBudgetSpent } from "./read-search-trace";

export type DebugReportInput = {
  conversationId: string;
  /** The API read, or none when it never arrived. */
  debug: ConversationDebugDTO | undefined;
  usage: ContextWindowUsage;
  llmId: LLMModels | undefined;
  rows: SegmentRow[];
  gap: ContextGap | null;
  incidents: Incident[];
};

const EMPTY = "—";

/**
 * The report's own labels, in English and hardcoded.
 *
 * Deliberately not the UI's translations: this document is read by an agent, not
 * by the user whose locale the panel follows, and `buildDebugMarkdown` in
 * `search-engine-debug-modal` draws the same line — its headings are English
 * while its interface is translated. Keeping them here also means the report
 * cannot break when a translation key moves.
 */
const SEGMENT_LABELS: Record<SegmentKey, string> = {
  system: "System prompt",
  tools: "Tool definitions",
  memory: "Memory (team + user)",
  knowledge: "Context data (knowledge base, attached companies)",
  tactical: "Turn-only instructions",
  history: "Previous messages",
  lastMessage: "Current message",
  free: "Free",
  overflow: "Over the window",
};

const COMPACTION_LABELS: Record<ConversationCompactionKind, string> = {
  "structural-prune": "Structural prune (tier 1)",
  "llm-summary": "LLM summary (tier 2)",
  boundary: "Compaction boundary",
};

const conversationSection = ({
  conversationId,
  debug,
  llmId,
}: DebugReportInput): string[] => [
  `# Chat debug — conversation ${conversationId}`,
  "",
  "## Conversation",
  // A title is not written by this product either: on creation it is a model's
  // output, from a schema with no length bound, driven by the user's first
  // message — itself able to quote a hostile page. Printed raw, it opened the
  // report with sections the reader takes for the real ones.
  `- Title: ${debug?.title ? asInlineCode(debug.title) : EMPTY}`,
  `- Conversation model: ${debug?.llmId ?? llmId ?? EMPTY}`,
  `- Created: ${debug ? formatDate(debug.createdAt) : EMPTY}`,
  `- Messages: ${debug?.messageCount ?? EMPTY}`,
  `- Still read by the model: ${debug?.contextMessageCount ?? EMPTY}`,
  "",
];

const contextSection = ({
  usage,
  llmId,
  rows,
  gap,
}: DebugReportInput): string[] => {
  const { estimatedTokensUsed, contextWindowSize, compactionTier } = usage;
  const lines = [
    "## Context of the last turn",
    `- Turn model: ${llmId ?? EMPTY}`,
    `- Compaction estimate: ${fullNumber(estimatedTokensUsed)} / ${fullNumber(contextWindowSize)} tokens (${formatPercent(estimatedTokensUsed, contextWindowSize)})`,
  ];

  if (gap) {
    lines.push(
      `- Breakdown total: ${fullNumber(gap.breakdownUsed)} tokens (${gap.breakdownPercent})`,
      `- Gap: ${formatDelta(gap.delta)} tokens — the breakdown also bills the tool definitions, which travel beside the messages.`
    );
  }

  lines.push(`- Compaction on this turn: ${compactionTier}`, "");

  if (rows.length === 0) {
    lines.push(
      "_No breakdown recorded for this message (stored before the feature existed)._",
      ""
    );
    return lines;
  }

  lines.push("| Block | Tokens | Share |", "| --- | ---: | ---: |");
  for (const row of rows) {
    lines.push(
      `| ${SEGMENT_LABELS[row.key]} | ${fullNumber(row.tokens)} | ${formatPercent(row.tokens, contextWindowSize)} |`
    );
  }
  lines.push("");

  return lines;
};

/**
 * Neutralises the Markdown structure of a text this report did not write.
 *
 * A compaction summary is a model's output and a memory entry is a tool's
 * input, and both land in a document made to be pasted into an agent. A fence
 * the summary opens and never closes turns every section below it into code,
 * and a line of its own starting with `## ` takes the rank of the report's real
 * headings — the reader is then handed incidents and a context breakdown that
 * the quoted text invented. Bounding the length does not help: the vector is in
 * the first characters, which is precisely what a bound keeps.
 */
const asQuotedBlock = (text: string): string =>
  text
    .split(LINE_ENDING)
    .map((line) => `> ${line.replace(/^(\s*)([#>|`~*_+-]|\d+\.)/, "$1\\$2")}`)
    .join("\n");

/** The same, for a value that has to stay on one line and inside its item. */
const asInlineCode = (text: string): string =>
  `\`${text.replace(/`/g, "'").replace(new RegExp(LINE_ENDING, "g"), " ")}\``;

const compactionSection = ({ debug }: DebugReportInput): string[] => {
  const compactions = debug?.compactions ?? [];
  const lines = [`## Compaction — ${compactions.length} event(s)`];

  if (compactions.length === 0) {
    lines.push("_No compaction on this conversation._", "");
    return lines;
  }

  lines.push("| Kind | When | Tokens at the turn |", "| --- | --- | ---: |");
  for (const event of compactions) {
    const tokens = event.estimatedTokensUsed
      ? fullNumber(event.estimatedTokensUsed)
      : EMPTY;
    lines.push(
      `| ${COMPACTION_LABELS[event.kind]} | ${formatDate(event.at)} | ${tokens} |`
    );
  }

  const summaries = compactions.filter((event) => event.summary);
  if (summaries.length > 0) {
    lines.push("", "### Summaries injected in place of the history");
    for (const event of summaries) {
      lines.push(
        "",
        `#### ${formatDate(event.at)} (${event.messageId})`,
        "",
        asQuotedBlock(event.summary ?? "")
      );
    }
  }
  lines.push("");

  return lines;
};

/** An incident's counts, `queries=3, pages=4`, or null when it has none. */
const formatLabelValues = (
  values: Record<string, number> | undefined
): string | null =>
  values
    ? Object.entries(values)
        .map(([name, value]) => `${name}=${value}`)
        .join(", ")
    : null;

const incidentsSection = ({ incidents }: DebugReportInput): string[] => {
  const lines = [`## Incidents — ${incidents.length}`];

  if (incidents.length === 0) {
    lines.push("_No cut turn, no error._", "");
    return lines;
  }

  lines.push(
    "| Scope | When | Message (start … end) | What | Detail | Cause | Retryable |",
    "| --- | --- | --- | --- | --- | --- | --- |"
  );
  for (const incident of incidents) {
    const quote =
      formatBounds(incident.contentStart, incident.contentEnd) ?? EMPTY;
    const retryable =
      incident.retryable === null
        ? EMPTY
        : incident.retryable
          ? "yes"
          : "no";
    lines.push(
      `| ${incident.scope} | ${incident.at} | ${escapeCell(quote)} | ${escapeCell(incident.labelKey.split(".").pop() ?? "")} | ${escapeCell(incident.detail ?? formatLabelValues(incident.labelValues) ?? EMPTY)} | ${escapeCell(incident.cause ?? EMPTY)} | ${retryable} |`
    );
  }
  lines.push("");

  return lines;
};

/**
 * The tool memories, as inputs and sizes.
 *
 * This report exists to be pasted into an agent, which is exactly why the
 * memories' values stay out of it: `scrapeWebsite` keeps the extracted text of
 * every page it read, and one copy would carry that third-party content — and
 * any personal data in it — to a service nobody chose for it.
 */
const toolMemorySection = ({ debug }: DebugReportInput): string[] => {
  const lines = ["## Tool memory"];

  if (!debug || debug.toolMemory.length === 0) {
    lines.push(
      "_No memory: no tool of this conversation has remembered anything yet._",
      ""
    );
    return lines;
  }

  lines.push(
    "_Remembered inputs only. What they returned stays in the database (`check:tool-memory`)._",
    "",
    "| Tool | Inputs | Size | Updated | Fields |",
    "| --- | ---: | ---: | --- | --- |"
  );
  for (const memory of debug.toolMemory) {
    const entryCount = memory.entries.length + memory.entriesOmitted;
    lines.push(
      `| ${memory.toolName} | ${fullNumber(entryCount)} | ${formatBytes(memory.sizeBytes)} | ${formatDate(memory.updatedAt)} | ${escapeCell(memory.fields.join(", "))} |`
    );
  }

  for (const memory of debug.toolMemory) {
    if (memory.entries.length === 0) continue;
    lines.push("", `### ${memory.toolName} — inputs`);
    for (const entry of memory.entries) {
      lines.push(`- ${asInlineCode(entry)}`);
    }
    if (memory.entriesOmitted > 0) {
      lines.push(
        `- _… and ${fullNumber(memory.entriesOmitted)} more, not listed._`
      );
    }
  }
  lines.push("");

  return lines;
};

const messagesSection = ({ debug }: DebugReportInput): string[] => {
  const lines = ["## Messages"];

  if (!debug || debug.messages.length === 0) {
    lines.push("_No message._");
    return lines;
  }

  lines.push(
    "| # | Role | Type | When | Model | Chars | Message (start … end) | Tools | Out of context |",
    "| ---: | --- | --- | --- | --- | ---: | --- | --- | --- |"
  );
  debug.messages.forEach((message, index) => {
    const tools =
      message.toolCalls
        .map((call) => `${call.type}${call.state ? `:${call.state}` : ""}`)
        .join(", ") || EMPTY;
    const quote =
      formatBounds(message.contentStart, message.contentEnd) ?? EMPTY;
    lines.push(
      `| ${index + 1} | ${message.role} | ${message.type} | ${formatDate(message.at)} | ${message.llmId} | ${message.contentLength} | ${escapeCell(quote)} | ${escapeCell(tools)} | ${message.isOutOfContext ? "yes" : "no"} |`
    );
  });

  return lines;
};

/**
 * One tool call: what it was asked, and what it gave back.
 *
 * Payloads are quoted rather than fenced. A fence is opened by three
 * characters and closed by three characters, and a tool output is free text
 * this report did not write — a page title holding ``` would end the block and
 * hand the rest of the conversation to the reader as prose.
 */
const toolCallLines = (
  call: ConversationDebugToolCallDTO,
  position: number
): string[] => {
  const lines = [
    "",
    `##### Tool ${position}: ${call.type.replace(/^tool-/, "")} — ${call.state ?? EMPTY}`,
  ];

  lines.push("", "Input:", asQuotedBlock(asJson(call.input)));

  if (call.output === undefined) {
    // Not a failure: `sanitizeAssistantParts` drops the output of most tools at
    // write time. Said plainly, because an empty "Output:" reads as a tool that
    // returned nothing.
    lines.push(
      "",
      "_Output not persisted — dropped at write time, not a failed call._"
    );
    return lines;
  }

  if (isSearchBudgetSpent(call.output)) {
    // Not a failure either: the answer had spent its search budget, so the
    // queries were skipped before reaching any engine.
    lines.push(
      "",
      "_Search budget spent — the queries were skipped, not failed._"
    );
  }

  lines.push("", "Output:", asQuotedBlock(asJson(call.output)));
  return lines;
};

/** A payload as readable JSON, or a plain marker when there is none. */
const asJson = (value: unknown): string => {
  if (value === undefined) return EMPTY;
  try {
    return JSON.stringify(value, null, 2) ?? String(value);
  } catch {
    // A cycle cannot reach a persisted payload — it went through JSON to get
    // into the database — but the report must not die on the one that does.
    return "_Unserializable payload._";
  }
};

/** The prompt of one turn, block by block, when the turn recorded it. */
const promptLines = (message: ConversationDebugMessageDTO): string[] => {
  const prompt = message.contextUsage?.prompt;
  if (!prompt) return [];

  const blocks: [string, string][] = [
    ["System prompt (memory included)", prompt.system],
    ["Memory (team + user)", prompt.memory],
    ["Knowledge base", prompt.knowledge],
    ["Interface note (turn-only instructions)", prompt.tactical],
  ];
  // Absent on turns recorded before the context data message existed.
  if (prompt.context !== undefined) {
    blocks.push([
      "Context data message (knowledge base and attached companies)",
      prompt.context,
    ]);
  }

  const lines = ["", "#### Prompt of this turn"];
  for (const [label, content] of blocks) {
    lines.push("", `##### ${label} — ${fullNumber(content.length)} chars`);
    lines.push(
      "",
      content.trim().length === 0 ? "_Empty._" : asQuotedBlock(content)
    );
  }
  return lines;
};

/**
 * Every turn in full: what was said, what the model thought, what it called and
 * with what, and the prompt it read to decide.
 *
 * The summary table above is an index; this is the conversation. Nothing here
 * is bounded — a turn cut in the middle is exactly the turn whose bug is not
 * visible, and an exact replay is the only thing this section is for.
 */
const transcriptSection = ({ debug }: DebugReportInput): string[] => {
  const lines = ["## Transcript"];

  if (!debug || debug.messages.length === 0) {
    lines.push("_No message._", "");
    return lines;
  }

  debug.messages.forEach((message, index) => {
    const flags = [
      message.type !== "REGULAR" ? message.type : null,
      message.isOutOfContext ? "out of context" : null,
    ].filter((flag): flag is string => !!flag);

    lines.push(
      "",
      `### ${index + 1}. ${message.role} — ${formatDate(message.at)} — ${message.llmId}${
        flags.length > 0 ? ` — ${flags.join(", ")}` : ""
      }`
    );

    if (message.reasoning.length > 0) {
      lines.push("", "#### Reasoning");
      for (const block of message.reasoning) {
        lines.push("", asQuotedBlock(block));
      }
    }

    if (message.toolCalls.length > 0) {
      lines.push("", "#### Tool calls");
      message.toolCalls.forEach((call, callIndex) => {
        lines.push(...toolCallLines(call, callIndex + 1));
      });
    }

    lines.push("", "#### Message");
    lines.push(
      "",
      message.content.trim().length === 0
        ? "_No prose: this turn spent everything on tool calls._"
        : asQuotedBlock(message.content)
    );

    lines.push(...promptLines(message));
  });

  lines.push("");
  return lines;
};

/**
 * The whole debug snapshot as one Markdown document.
 *
 * Written to be pasted into an agent, and it carries the conversation itself:
 * every message in full, the model's reasoning, each tool call with its
 * payloads, and the prompt each turn ran with. That is the point of the
 * document — a diagnosis made from metadata alone was guesswork — and it is why
 * the panel behind it is admin-only and scoped to the reader's own
 * conversations.
 *
 * Nothing is truncated. The one thing still done to text this report did not
 * write is quoting it: a heading or a code fence inside a message would
 * otherwise take over the structure of the document that carries it, and the
 * reader would be handed incidents and a breakdown the quoted text invented.
 *
 * Sections are the panel's own tabs, in the same order, with the full
 * transcript last — it is the longest by far, and everything above it reads as
 * a summary of it.
 */
export const buildDebugReport = (input: DebugReportInput): string =>
  [
    ...conversationSection(input),
    ...contextSection(input),
    ...compactionSection(input),
    ...incidentsSection(input),
    ...toolMemorySection(input),
    ...messagesSection(input),
    "",
    ...transcriptSection(input),
  ].join("\n");
